import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SITE } from '@/lib/site';
import { AdminAuth } from '@/lib/api';
import { useSeo } from '@/lib/seo';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

export default function AdminLogin() {
    useSeo({ title: 'Floreal — Area riservata', description: 'Accesso amministratore Floreal Atelier del Fiore.' });
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // If already authenticated (httpOnly cookie is valid), jump to dashboard
        let cancelled = false;
        AdminAuth.check().then((ok) => {
            if (!cancelled && ok) navigate('/admin/dashboard', { replace: true });
        });
        return () => { cancelled = true; };
    }, [navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!password) { setError('Inserisci la password'); return; }
        setLoading(true);
        try {
            await AdminAuth.login(password);
            navigate('/admin/dashboard', { replace: true });
        } catch (err) {
            const msg = errorMessage(err, 'Password non valida');
            setError(typeof msg === 'string' ? msg : 'Errore di accesso');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-atelier-cream flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <a href="/" className="inline-flex items-baseline gap-3 mb-8">
                    <span className="font-serif text-3xl tracking-[0.18em] uppercase font-medium">Floreal</span>
                    <span className="eyebrow text-muted-foreground tracking-[0.18em]">Atelier del Fiore</span>
                </a>
                <div className="rounded-xl bg-card border border-border p-7 sm:p-9">
                    <p className="eyebrow mb-2">Area riservata</p>
                    <h1 className="font-serif text-3xl tracking-tight">Accedi al pannello</h1>
                    <p className="text-sm text-muted-foreground mt-2">Riservato all'amministratore di Floreal Atelier.</p>
                    <form data-testid={TEST_IDS.adminLoginForm} onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <Label htmlFor="pwd" className="eyebrow">Password</Label>
                            <div className="relative mt-1.5">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input id="pwd" data-testid={TEST_IDS.adminPasswordInput} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={cn('pl-9 h-11', error && 'border-destructive')} autoFocus />
                            </div>
                            {error && (
                                <p className="flex items-center gap-1.5 text-xs text-destructive mt-2">
                                    <AlertCircle className="h-3.5 w-3.5" /> {error}
                                </p>
                            )}
                        </div>
                        <Button data-testid={TEST_IDS.adminLoginSubmit} type="submit" className="w-full h-11" disabled={loading}>
                            {loading ? 'Verifica…' : 'Accedi'}
                        </Button>
                    </form>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-6">
                    <a href="/" className="hover:text-foreground transition-colors">← Torna al sito</a>
                </p>
            </div>
        </div>
    );
}
